import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RelatedEventsPanel = ({ currentDrug, className = '' }) => {
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (currentDrug) {
      loadRelatedEvents();
    }
  }, [currentDrug]);

  const loadRelatedEvents = async () => {
    setLoading(true);
    
    // Simulate API call to fetch related events
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const mockEvents = [
      {
        id: 'AE-2024-1245',
        drugName: currentDrug || 'Metformin',
        patientAge: 'Adult (45 years)',
        severity: 'moderate',
        outcome: 'recovered',
        description: 'Gastrointestinal upset with nausea and diarrhea',
        reportDate: '2024-01-15',
        reporterType: 'Healthcare Professional',
        country: 'Greece',
        status: 'submitted'
      },
      {
        id: 'AE-2024-1198',
        drugName: currentDrug || 'Metformin',
        patientAge: 'Elderly (72 years)',
        severity: 'mild',
        outcome: 'recovered',
        description: 'Mild stomach discomfort after meals',
        reportDate: '2024-01-08',
        reporterType: 'Patient',
        country: 'Germany',
        status: 'under_review'
      },
      {
        id: 'AE-2024-1156',
        drugName: currentDrug || 'Metformin',
        patientAge: 'Adult (38 years)',
        severity: 'severe',
        outcome: 'recovering',
        description: 'Lactic acidosis requiring hospitalization',
        reportDate: '2023-12-28',
        reporterType: 'Healthcare Professional',
        country: 'France',
        status: 'closed'
      },
      {
        id: 'AE-2024-1089',
        drugName: currentDrug || 'Metformin',
        patientAge: 'Adult (55 years)',
        severity: 'moderate',
        outcome: 'not_recovered',
        description: 'Persistent metallic taste and appetite loss',
        reportDate: '2023-12-20',
        reporterType: 'Healthcare Professional',
        country: 'Italy',
        status: 'submitted'
      }
    ];

    setRelatedEvents(mockEvents);
    setLoading(false);
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'mild':
        return { color: 'text-success', bgColor: 'bg-success/10', icon: 'Info' };
      case 'moderate':
        return { color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertCircle' };
      case 'severe':
        return { color: 'text-error', bgColor: 'bg-error/10', icon: 'AlertTriangle' };
      case 'life_threatening':
        return { color: 'text-destructive', bgColor: 'bg-destructive/10', icon: 'Skull' };
      default:
        return { color: 'text-muted-foreground', bgColor: 'bg-muted/10', icon: 'Circle' };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'submitted':
        return { color: 'text-primary', label: 'Submitted' };
      case 'under_review':
        return { color: 'text-warning', label: 'Under Review' };
      case 'closed':
        return { color: 'text-success', label: 'Closed' };
      default:
        return { color: 'text-muted-foreground', label: 'Unknown' };
    }
  };

  const filteredEvents = relatedEvents?.filter(event => {
    if (filter === 'all') return true;
    if (filter === 'severe') return event?.severity === 'severe' || event?.severity === 'life_threatening';
    if (filter === 'recent') return new Date(event.reportDate) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return event?.severity === filter;
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Database" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Related Events</h3>
            <p className="text-sm text-muted-foreground">
              Similar adverse events for {currentDrug || 'this drug'}
            </p>
          </div>
        </div>
        
        {/* Filter Dropdown */}
        <select
          value={filter}
          onChange={(e) => setFilter(e?.target?.value)}
          className="text-sm border border-border rounded-md px-3 py-1 bg-input text-foreground"
        >
          <option value="all">All Events</option>
          <option value="severe">Severe Only</option>
          <option value="recent">Recent (30 days)</option>
          <option value="mild">Mild</option>
          <option value="moderate">Moderate</option>
        </select>
      </div>
      {loading ? (
        <div className="text-center py-8">
          <Icon name="Loader" size={32} className="text-muted-foreground animate-spin mx-auto mb-3" />
          <p className="text-muted-foreground">Loading related events...</p>
        </div>
      ) : filteredEvents?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Search" size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No related events found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEvents?.map((event) => {
            const severityConfig = getSeverityConfig(event?.severity);
            const statusConfig = getStatusConfig(event?.status);
            
            return (
              <div
                key={event?.id}
                className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors duration-150"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`p-1.5 rounded-full ${severityConfig?.bgColor}`}>
                      <Icon name={severityConfig?.icon} size={14} className={severityConfig?.color} />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{event?.id}</h4>
                      <p className="text-sm text-muted-foreground">
                        {event?.patientAge} • {formatDate(event?.reportDate)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full bg-muted ${statusConfig?.color} font-medium`}>
                      {statusConfig?.label}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${severityConfig?.bgColor} ${severityConfig?.color} font-medium uppercase`}>
                      {event?.severity}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-foreground mb-3 line-clamp-2">
                  {event?.description}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center space-x-4">
                    <span>Reporter: {event?.reporterType}</span>
                    <span>Country: {event?.country}</span>
                    <span>Outcome: {event?.outcome?.replace('_', ' ')}</span>
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="ExternalLink"
                    iconPosition="right"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Summary Statistics */}
      {!loading && filteredEvents?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-foreground">
                {filteredEvents?.length}
              </p>
              <p className="text-xs text-muted-foreground">Total Events</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-error">
                {filteredEvents?.filter(e => e?.severity === 'severe')?.length}
              </p>
              <p className="text-xs text-muted-foreground">Severe</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-success">
                {filteredEvents?.filter(e => e?.outcome === 'recovered')?.length}
              </p>
              <p className="text-xs text-muted-foreground">Recovered</p>
            </div>
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex space-x-3 mt-6 pt-4 border-t border-border">
        <Button
          variant="outline"
          iconName="Download"
          iconPosition="left"
          className="flex-1"
        >
          Export Report
        </Button>
        <Button
          variant="outline"
          iconName="TrendingUp"
          iconPosition="left"
          className="flex-1"
        >
          View Analytics
        </Button>
      </div>
    </div>
  );
};

export default RelatedEventsPanel;